import React, { useEffect, useMemo, useState } from "react";
import { IProduct } from "integrations/firebase/collections";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { Button } from "components/button";
import { routePath } from "shared/constants";
import { TabHeader } from "components/TabHeader";
import { SearchResultsIndicator } from "components/SearchResultsIndicator";
import { Separator } from "components/ui/separator";
import { format } from "date-fns";
import { Icon } from "components/icon/Icon";
import { useFeatureFlags } from "contexts/FeatureFlagContext";
import { useProjectDetails } from "./contexts/ProjectDetailsContext";
import { getProjectProducts, productMatchesQuery } from "./utils";
import { ErrorMessage } from "./components/ErrorMessage";
import { DetailsTable } from "./table/DetailsTable";
import { ProjectEditISBNDialog } from "./components/ProjectEditISBNDialog";
import { OpenAmplifyProjectButton } from "./components/OpenAmplifyProjectButton";

export const ProjectDetails: React.FC = () => {
  const {
    setProductsToExport,
    onRowClickCallback,
    isLoadingRead,
    errorRead,
    products,
    isLoadingProducts,
    project,
    updateProjectName,
    errorProjectRead,
  } = useProjectDetails();

  const { t } = useTranslation(["projectDetails"]);
  const { features } = useFeatureFlags();
  const navigate = useNavigate();
  const fromUrl = useLocation({ select: (location) => location.state.from });

  const [searchQuery, setSearchQuery] = useState("");

  const isLoading = useMemo(
    () => isLoadingRead || isLoadingProducts,
    [isLoadingProducts, isLoadingRead],
  );

  const totalCount = products?.length ?? 0;
  const matchingCount = useMemo(
    () =>
      (products ?? []).filter((product) =>
        productMatchesQuery(product, searchQuery),
      ).length,
    [products, searchQuery],
  );

  const changeProjectName = (name: string) => {
    if (project?.id) {
      updateProjectName(project?.id, name);
    }
  };

  useEffect(() => {
    if (isLoading || !project || !products) {
      return;
    }
    const projectProducts = getProjectProducts(project, products) as IProduct[];

    setProductsToExport(projectProducts.filter((p) => p.isbn !== null));

    return () => {
      setProductsToExport([]);
    };
  }, [isLoading, project, products, setProductsToExport]);

  if ((errorRead || errorProjectRead) && !isLoading) {
    return <ErrorMessage backLink={routePath("/projects")} />;
  }

  const created = project?.createdAt
    ? format(project?.createdAt, "MMM dd, yyyy")
    : undefined;

  return (
    <div className="space-y-6">
      <div className="flex flex-row justify-between items-start gap-2">
        <div>
          {fromUrl?.startsWith(routePath("/books")) ? (
            <Button
              variant="ghost"
              label={t("projectDetails:buttons.books")}
              icon="arrow-left"
              testId="button-books-back"
              className="-ml-4"
              onClick={() => navigate({ href: fromUrl })}
            />
          ) : (
            <Button
              variant="ghost"
              label={t("projectDetails:buttons.projects")}
              icon="arrow-left"
              testId="button-projects-back"
              className="-ml-4"
              onClick={() => navigate({ to: "/projects" })}
            />
          )}

          <TabHeader
            title={project?.name || ""}
            onEdit={changeProjectName}
            subtitle={
              <div className="flex flex-row gap-2">
                <SearchResultsIndicator
                  count={matchingCount}
                  total={isLoading ? null : totalCount}
                  isLoading={isLoading}
                  tooltip={t("projectDetails:header.subtitle.tooltip")}
                  testId="project-books-count"
                />
                <Separator
                  orientation="vertical"
                  className="data-[orientation=vertical]:h-auto data-[orientation=vertical]:w-0.5"
                />
                <div className="flex items-center">
                  <Icon name="calendar" size="small" iconClassName="mr-1.5" />
                  <div>{created}</div>
                </div>
              </div>
            }
            isEditable
          />
        </div>

        <div className="flex flex-start gap-2">
          {features.amplifyProjects ? (
            <OpenAmplifyProjectButton
              amplifyProjectId={project?.amplifyProjectId}
            />
          ) : null}
          <ProjectEditISBNDialog />
        </div>
      </div>

      <DetailsTable
        onRowClick={onRowClickCallback}
        isLoading={isLoading}
        products={products || []}
        projectProductIds={project?.productIds ?? []}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
      />
    </div>
  );
};
